import {
  type BaseEditor,
  type BaseElement,
  type BaseRange,
  type BaseText,
  type Descendant,
  Editor as SlateEditor,
  Element as SlateElement,
  type Node,
  Path as SlatePath,
  type Point,
  Range as SlateRange,
  Transforms as SlateTransforms,
} from 'slate';
import { DOMEditor } from 'slate-dom';
import { ReactEditor } from 'slate-react';

import {
  SLATE_EMPTY_SELECTION_COPY,
  SLATE_TEXT_FORMATS,
  type SlateEmptySelectionCopy,
} from './const';
import { getEditorHtmlContent, getEditorTextContent, isEditorEmpty } from './queries';
import { slateToHtml, slateToText } from './serialize';
import type { CustomSlateElement } from './types';

export const focusEditorDom = (editor: DOMEditor): void => {
  const element = DOMEditor.toDOMNode(editor, editor);
  element.focus({ preventScroll: true });
};

export const blurEditorDom = (editor: DOMEditor): void => {
  const element = DOMEditor.toDOMNode(editor, editor);
  element.blur();
};

export const focusEditor = (
  editor: BaseEditor,
  options?: { atEnd?: boolean; ignoreSelection?: boolean }
) => {
  try {
    const shouldApplySelection = options?.ignoreSelection || !editor?.selection;
    if (!isEditorEmpty(editor) && shouldApplySelection) {
      const path = options?.atEnd ? SlateEditor.end(editor, []) : SlateEditor.start(editor, []);
      SlateTransforms.select(editor, path);
    }
  } catch {
    // don't do anything
  }

  setTimeout(() => {
    try {
      focusEditorDom(editor as DOMEditor);
    } catch {
      // don't do anything
    }
  }, 50);
};

export const blurEditor = (editor: BaseEditor) => {
  try {
    blurEditorDom(editor as DOMEditor);
  } catch {
    // don't do anything
  }
};

export const editorHasValidSelection = (editor: SlateEditor): boolean => {
  const { selection } = editor;
  return (
    selection != null &&
    !SlateRange.isCollapsed(selection) &&
    SlateEditor.string(editor, selection).length > 0
  );
};

export const editorIsFocusedAndHasValidSelection = (editor: ReactEditor): boolean => {
  return ReactEditor.isFocused(editor) && editorHasValidSelection(editor);
};

/**
 * Select the whole editor content
 * @param editor
 */
export const selectWholeEditor = (editor: ReactEditor): void => {
  ReactEditor.focus(editor);
  SlateTransforms.select(editor, []);
};

/**
 * Select the current line
 * @param editor
 */
export const selectEditorLine = (editor: SlateEditor): void => {
  SlateTransforms.move(editor, {
    distance: 1,
    unit: 'line',
    reverse: true,
  });
  const startPoint = editor.selection ? editor.selection.anchor : null;

  SlateTransforms.move(editor, {
    distance: 1,
    unit: 'line',
    reverse: false,
  });
  const endPoint = editor.selection ? editor.selection.focus : null;

  if (!startPoint || !endPoint) {
    return;
  }

  SlateTransforms.select(editor, { anchor: startPoint, focus: endPoint });
};

/**
 * Select the currently focused leaf node
 * @param editor
 */
export const selectFocusedLeaf = (editor: SlateEditor): void => {
  const path = editor.selection?.anchor?.path;
  if (!path) {
    return;
  }

  const pathClone = [...path];
  pathClone.pop();

  const focusedNode = pathClone?.reduce((node: Descendant | null, pathPosition) => {
    if (!node) {
      return editor.children[pathPosition];
    }

    const element = node as SlateElement;
    return element.children ? element.children[pathPosition] : null;
  }, null);
  const focusedElement = focusedNode as BaseElement;
  const firstLeaf = focusedElement?.children?.[0] as BaseText;

  if (!editor.selection || !firstLeaf) {
    return;
  }

  SlateTransforms.select(editor, {
    anchor: { path: editor.selection.anchor.path, offset: 0 },
    focus: { path: editor.selection.focus.path, offset: firstLeaf.text.length },
  });
};

/**
 * Select the whole editor node
 * @param editor
 * @param node
 */
export const selectWholeNode = (editor: DOMEditor, node: Node): void => {
  const path = ReactEditor.findPath(editor, node);
  try {
    if (SlateEditor.hasPath(editor, path)) {
      // Clear existing selections
      SlateTransforms.deselect(editor);

      // Select the new range
      SlateTransforms.select(editor, SlateEditor.range(editor, path));

      // Focus the editor
      ReactEditor.focus(editor);
    }
  } catch {
    // do nothing
  }
};

/**
 * Place cursor at the start of the new content
 * @param editor
 */
export const placeCursorAtStart = (editor: SlateEditor): void => {
  if (editor.isEmpty(editor)) {
    return;
  }

  const point = SlateEditor.start(editor, []);
  SlateTransforms.select(editor, point);
};

/**
 * Place cursor at the end of the new content
 * @param editor
 */
export const placeCursorAtEnd = (editor: SlateEditor): void => {
  if (editor.isEmpty(editor)) {
    return;
  }

  const point = SlateEditor.end(editor, []);
  SlateTransforms.select(editor, point);
};

/**
 * Convert Slate range to DOM range
 * @param editor
 * @param slateRange
 */
export const convertSlateRangeToDomRange = (
  editor: DOMEditor,
  slateRange?: BaseRange
): Range | undefined => {
  try {
    if (!slateRange) {
      return undefined;
    }

    if (!ReactEditor.hasRange(editor, slateRange)) {
      return undefined;
    }

    return ReactEditor.toDOMRange(editor, slateRange);
  } catch {
    return undefined;
  }
};

const getLowestBlockPath = (editor: SlateEditor, point: Point): SlatePath | undefined =>
  SlateEditor.above(editor, {
    at: point,
    match: (node) => SlateElement.isElement(node) && SlateEditor.isBlock(editor, node),
  })?.[1];

const hasNoTextBetween = (editor: SlateEditor, from: Point, to: Point): boolean =>
  SlateEditor.string(editor, { anchor: from, focus: to }) === '';

/**
 * A browser can stretch a selection edge into the next or the previous block. Such an edge holds
 * no text, but it adds an empty block to the copy.
 */
const trimHangingEdges = (editor: SlateEditor, range: BaseRange): BaseRange => {
  let [start, end] = SlateRange.edges(range);

  const endBlockPath = getLowestBlockPath(editor, end);
  if (endBlockPath && !SlatePath.isAncestor(endBlockPath, start.path)) {
    const endBlockStart = SlateEditor.start(editor, endBlockPath);
    if (hasNoTextBetween(editor, endBlockStart, end)) {
      end = SlateEditor.before(editor, endBlockStart) ?? end;
    }
  }

  const startBlockPath = getLowestBlockPath(editor, start);
  if (startBlockPath && !SlatePath.isAncestor(startBlockPath, end.path)) {
    const startBlockEnd = SlateEditor.end(editor, startBlockPath);
    if (hasNoTextBetween(editor, start, startBlockEnd)) {
      start = SlateEditor.after(editor, startBlockEnd) ?? start;
    }
  }

  return { anchor: start, focus: end };
};

const coversBlocks = (
  editor: SlateEditor,
  { start, end }: { start: Point; end: Point },
  startBlockPath: SlatePath,
  endBlockPath: SlatePath
): boolean =>
  hasNoTextBetween(editor, SlateEditor.start(editor, startBlockPath), start) &&
  hasNoTextBetween(editor, end, SlateEditor.end(editor, endBlockPath));

/**
 * A fragment repeats every ancestor of the selection, so the content of a block at depth N sits N
 * levels down.
 */
const unwrapFragment = (fragment: Descendant[], depth: number): Descendant[] => {
  if (depth === 0) {
    return fragment;
  }

  const children = fragment.flatMap((node) =>
    SlateElement.isElement(node) ? node.children : [node]
  );
  return unwrapFragment(children, depth - 1);
};

/**
 * An asset's wrapper carries the asset's id and score. If a user pastes the wrapper back, the
 * document gets a second copy of the asset around part of its text.
 */
const stripVariationWrappers = (nodes: Descendant[]): Descendant[] =>
  nodes.flatMap((node) => {
    if (!SlateElement.isElement(node)) {
      return [node];
    }

    const children = stripVariationWrappers(node.children);
    if ((node as CustomSlateElement).type === SLATE_TEXT_FORMATS.DD_VARIATION) {
      return children;
    }

    return [{ ...node, children } as Descendant];
  });

/**
 * - Whole top-level blocks keep all their markup, so a whole asset keeps its wrapper.
 * - The copy of part of one block holds inline content only. It pastes into a line, not as a
 *   new paragraph.
 * - Anything else keeps its blocks and their lists, but not the wrappers of the assets it cuts
 *   through.
 */
const getRangeHtml = (editor: SlateEditor, range: BaseRange, fragment: Descendant[]): string => {
  const [start, end] = SlateRange.edges(range);
  const edges = { start, end };

  if (coversBlocks(editor, edges, [start.path[0]], [end.path[0]])) {
    return slateToHtml(fragment);
  }

  const startBlockPath = getLowestBlockPath(editor, start);
  const endBlockPath = getLowestBlockPath(editor, end);
  const isInOneBlock =
    startBlockPath && endBlockPath && SlatePath.equals(startBlockPath, endBlockPath);

  if (isInOneBlock && !coversBlocks(editor, edges, startBlockPath, endBlockPath)) {
    return slateToHtml(unwrapFragment(fragment, startBlockPath.length));
  }

  return slateToHtml(stripVariationWrappers(fragment));
};

export interface EditorCopyContent {
  html: string;
  text: string;
}

interface EditorCopyOptions {
  emptySelection?: SlateEmptySelectionCopy;
}

/**
 * The content a copy from the editor puts on the clipboard. Both formats must carry the same
 * content: a Google Sheets cell in edit mode reads only the plain text.
 */
export const getEditorCopyContent = (
  editor: SlateEditor,
  { emptySelection = SLATE_EMPTY_SELECTION_COPY.nothing }: EditorCopyOptions = {}
): EditorCopyContent => {
  const { selection } = editor;
  const range =
    selection && !SlateRange.isCollapsed(selection) ? trimHangingEdges(editor, selection) : null;

  if (!range || SlateRange.isCollapsed(range)) {
    if (emptySelection === SLATE_EMPTY_SELECTION_COPY.wholeContent) {
      return { html: getEditorHtmlContent(editor), text: getEditorTextContent(editor) };
    }

    return { html: '', text: '' };
  }

  const fragment = SlateEditor.fragment(editor, range);
  return { html: getRangeHtml(editor, range, fragment), text: slateToText(fragment) };
};
